import { Command } from 'cmdk';
import {
  BookOpen,
  CalendarClock,
  ChevronRight,
  FileText,
  Loader2,
  Package as PackageIcon,
  RotateCcw,
  Search,
  User as UserIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { canSeeNavItem } from './app-variant';
import { NAV_ITEMS, type NavItemConfig } from './nav-config';

import type { usePermissions } from '@/features/website-cms/lib';
import type { LeadListItem, UserRole } from '@utazzo/shared';
import type { LucideIcon } from 'lucide-react';

import { useCurrentUser } from '@/features/auth/use-auth';
import { useGlobalSearch } from '@/features/global-search/use-global-search';
import { usePaletteInbox } from '@/features/global-search/use-palette-inbox';
import { useMyEffectivePermissions } from '@/features/my-profile/use-my-profile';
import { useNavMru } from '@/features/nav-mru/use-nav-mru';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  role: ReturnType<typeof usePermissions>['role'];
  can: ReturnType<typeof usePermissions>['can'];
  /** Optional value used to seed the input when the palette opens. */
  initialQuery?: string;
}

const canAccess = canSeeNavItem;

const KIND_LABELS: Record<'leads' | 'customers' | 'packages' | 'blogs', string> = {
  leads: 'Leads',
  customers: 'Customers',
  packages: 'Packages',
  blogs: 'Blogs',
};

const KIND_ICONS: Record<'leads' | 'customers' | 'packages' | 'blogs', LucideIcon> = {
  leads: FileText,
  customers: UserIcon,
  packages: PackageIcon,
  blogs: BookOpen,
};

/**
 * Per-kind icon chip styling. Backgrounds use the neutral zinc surface
 * (`--z-100`) - matching the rest of the admin's restrained palette - and
 * the icon itself carries the colour. Keeps the look in line with the
 * "95% neutral zinc, colour used sparingly" design rule.
 */
const KIND_CHIP: Record<'leads' | 'customers' | 'packages' | 'blogs', string> = {
  customers: 'bg-[var(--z-100)] text-emerald-600',
  leads: 'bg-[var(--z-100)] text-[var(--accent-600)]',
  packages: 'bg-[var(--z-100)] text-violet-600',
  blogs: 'bg-[var(--z-100)] text-amber-600',
};

/** Follow-up = soft warn (amber), Revert = soft error (red), both matching
 *  the project's semantic tokens so DueChip and the row chip stay coherent. */
const FOLLOWUP_CHIP = 'bg-[var(--ds-warn-bg)] text-[var(--ds-warn)]';
const REVERT_CHIP = 'bg-[var(--ds-error-bg)] text-[var(--ds-error)]';
const QUICKLINK_CHIP = 'bg-[var(--z-100)] text-[var(--fg-muted)]';
const NAV_CHIP = 'bg-[var(--z-100)] text-[var(--fg-muted)]';

type Accent = 'amber' | 'rose' | 'sky' | 'emerald' | 'violet' | 'slate';

const ACCENT_DOT: Record<Accent, string> = {
  amber: 'bg-[var(--ds-warn)]',
  rose: 'bg-[var(--ds-error)]',
  sky: 'bg-[var(--accent-600)]',
  emerald: 'bg-emerald-500',
  violet: 'bg-violet-500',
  slate: 'bg-[var(--z-400)]',
};

const KIND_ACCENT: Record<'leads' | 'customers' | 'packages' | 'blogs', Accent> = {
  leads: 'sky',
  customers: 'emerald',
  packages: 'violet',
  blogs: 'amber',
};

/**
 * Per-role "Quick links" set shown when the palette opens with no query.
 *
 * Sales-facing roles get the minimal 3 (Leads / Customers / Itineraries) so
 * the empty palette stays a productivity launcher, not a menu dump. Other
 * roles get the set that matches their day - Ops adds inventory anchors,
 * Admins get the CMS anchors, Finance + ReadOnly stay narrow.
 *
 * Items the operator can't access are filtered out before render, so a
 * SALES_AGENT viewing the "ADMIN" fallback set would still see only what
 * their permissions allow.
 */
const PINNED_BY_ROLE: Record<UserRole, ReadonlyArray<string>> = {
  ADMIN: ['leads', 'customers', 'packages', 'blogs', 'itineraries'],
  SALES_LEAD: ['leads', 'customers', 'itineraries'],
  SALES_AGENT: ['leads', 'customers', 'itineraries'],
  OPS: ['leads', 'customers', 'itineraries', 'packages', 'hotels'],
  FINANCE: ['leads', 'customers'],
  HR: ['users', 'branches', 'departments'],
  READ_ONLY: ['leads', 'customers'],
  CONTENT_EDITOR: ['blogs', 'pages', 'media', 'navigation'],
  MARKETING_LEAD: ['blogs', 'pages', 'partners', 'press', 'navigation'],
  WEBSITE_MANAGER: ['packages', 'pages', 'blogs', 'destination-pages', 'category-pages'],
  WEBSITE_SEO: ['packages', 'pages', 'blogs', 'destination-pages', 'category-pages'],
  // 2026-07-09 (ADR-0063). Org-chart splits + back-office desks.
  HR_MANAGER: ['users', 'branches', 'departments'],
  HR_EXECUTIVE: ['users', 'branches', 'departments'],
  OPERATIONS_LEAD: ['leads', 'customers', 'itineraries', 'packages', 'hotels'],
  OPERATIONS_EXECUTIVE: ['leads', 'customers', 'itineraries', 'hotels'],
  VISA: ['leads', 'customers', 'visas', 'itineraries'],
  TICKETING: ['leads', 'customers', 'airlines', 'itineraries'],
};

const DEFAULT_PINNED: ReadonlyArray<string> = ['leads', 'customers', 'itineraries'];

const formatFollowUpDue = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(d);
  due.setHours(0, 0, 0, 0);
  const days = Math.round((due.getTime() - today.getTime()) / 86400000);
  if (days < 0) return `Overdue ${Math.abs(days)}d`;
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  return `Due in ${days}d`;
};

/**
 * Global ⌘K palette.
 *
 * Empty query (operator productivity view):
 *   1. Today's follow-ups - RBAC-scoped from `/leads?followUpDue=true`
 *   2. Revert pending     - RBAC-scoped from `/leads?revertPending=true`
 *   3. Quick links        - role-based pinned menu shortcuts (3-5 items)
 *
 * Typing ≥2 chars:
 *   1. Customers / Leads / Packages / Blogs - content search results
 *   2. Navigate - full role-filtered menu, fuzzy-matched against input
 */
export const CommandPalette = ({
  open,
  onClose,
  role,
  can,
  initialQuery = '',
}: CommandPaletteProps): React.JSX.Element | null => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { bump } = useNavMru();
  const effectivePermissions = useMyEffectivePermissions();
  const currentUser = useCurrentUser();

  const [query, setQuery] = useState(initialQuery);
  const [debounced, setDebounced] = useState(initialQuery);

  // Reset input when the palette is opened with an initialQuery hint.
  useEffect(() => {
    if (open) {
      setQuery(initialQuery);
      setDebounced(initialQuery);
    }
  }, [open, initialQuery]);

  // Debounce typing -> the value the search query actually sees.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  // Esc to close (cmdk handles ↑/↓/Enter).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const isSearching = debounced.trim().length >= 2;

  const search = useGlobalSearch(debounced);
  const inbox = usePaletteInbox(open && !isSearching);

  const navItems = useMemo(
    () =>
      NAV_ITEMS.filter(
        (i) =>
          !i.hiddenFromNav &&
          canAccess(
            i,
            role,
            can,
            effectivePermissions,
            currentUser?.email,
            currentUser?.assignedRoleCodes,
          ),
      ),
    [role, can, effectivePermissions, currentUser?.email, currentUser?.assignedRoleCodes],
  );

  // Quick-links set: role-driven, then filtered to what the operator can
  // actually access, then capped at 5 so the strip stays a launcher.
  const quickLinks = useMemo(() => {
    const byId = new Map<string, NavItemConfig>(navItems.map((i) => [i.id, i]));
    const ids = role && role in PINNED_BY_ROLE ? PINNED_BY_ROLE[role] : DEFAULT_PINNED;
    return ids
      .map((id) => byId.get(id))
      .filter((i): i is NavItemConfig => Boolean(i))
      .slice(0, 5);
  }, [role, navItems]);

  const go = (to: string, navId?: string): void => {
    if (navId) bump(navId);
    navigate(to);
    onClose();
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-start justify-center pt-[12vh]"
      style={{ animation: 'fadeUp 0.15s var(--ease) both' }}
    >
      <button
        type="button"
        aria-label={t('shell.command.hintClose')}
        onClick={onClose}
        className="absolute inset-0 bg-[var(--z-950)]/40"
      />
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events -- backdrop button above handles dismiss; this stop-propagation keeps clicks inside the dialog from bubbling. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('shell.command.placeholder')}
        onClick={(e) => e.stopPropagation()}
        className="relative flex max-h-[70vh] w-[600px] max-w-[92vw] flex-col overflow-hidden rounded-[var(--r-xl)] border border-[var(--ds-border)] bg-[var(--bg)] text-left shadow-[var(--shadow-lg)]"
      >
        <Command
          label={t('shell.command.placeholder')}
          loop
          // When searching we let cmdk fuzzy-filter the Navigate group on
          // the query. When the query is empty (productivity view) we
          // disable cmdk filtering so the inbox + Quick links render in
          // their server-defined order, not cmdk's match-score order.
          shouldFilter={isSearching}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="flex items-center gap-2 border-b border-[var(--ds-border)] px-3.5 py-3">
            <Search className="h-[14px] w-[14px] text-[var(--fg-faint)]" />
            <Command.Input
              autoFocus
              value={query}
              onValueChange={setQuery}
              placeholder={t('shell.command.placeholder')}
              className="flex-1 border-0 bg-transparent text-[15px] text-[var(--fg)] outline-none placeholder:text-[var(--fg-faint)]"
            />
            {(search.isFetching && isSearching) || (inbox.isLoading && !isSearching) ? (
              <Loader2 className="h-[14px] w-[14px] animate-spin text-[var(--fg-faint)]" />
            ) : null}
            <span className="kbd">Esc</span>
          </div>
          <Command.List className="min-h-0 flex-1 overflow-y-auto px-1.5 py-2">
            <Command.Empty className="px-3 py-9 text-center text-[14px] text-[var(--fg-faint)]">
              {t('shell.command.empty')}
            </Command.Empty>

            {/* ===== Empty-query productivity view ===== */}
            {!isSearching && (
              <>
                {inbox.todaysFollowUps.length > 0 && (
                  <Command.Group
                    heading={
                      <GroupHeader
                        accent="amber"
                        label="Today's follow-ups"
                        count={inbox.todaysFollowUps.length}
                      />
                    }
                    className={GROUP_CLASS}
                  >
                    {inbox.todaysFollowUps.map((lead) => (
                      <PaletteLeadRow
                        key={`fu-${lead.id}`}
                        lead={lead}
                        icon={CalendarClock}
                        chipClass={FOLLOWUP_CHIP}
                        showFollowUpDue
                        onSelect={() => go(`/leads/${lead.id}`)}
                      />
                    ))}
                  </Command.Group>
                )}

                {inbox.revertPending.length > 0 && (
                  <Command.Group
                    heading={
                      <GroupHeader
                        accent="rose"
                        label="Revert pending"
                        count={inbox.revertPending.length}
                      />
                    }
                    className={GROUP_CLASS}
                  >
                    {inbox.revertPending.map((lead) => (
                      <PaletteLeadRow
                        key={`rp-${lead.id}`}
                        lead={lead}
                        icon={RotateCcw}
                        chipClass={REVERT_CHIP}
                        onSelect={() => go(`/leads/${lead.id}`)}
                      />
                    ))}
                  </Command.Group>
                )}

                {quickLinks.length > 0 && (
                  <Command.Group
                    heading={
                      <GroupHeader accent="slate" label="Quick links" count={quickLinks.length} />
                    }
                    className={GROUP_CLASS}
                  >
                    {quickLinks.map((item) => (
                      <PaletteNavRow
                        key={`ql-${item.id}`}
                        item={item}
                        label={t(`shell.nav.${item.id}`)}
                        chipClass={QUICKLINK_CHIP}
                        onSelect={() => go(item.to, item.id)}
                      />
                    ))}
                  </Command.Group>
                )}
              </>
            )}

            {/* ===== Search view (≥2 chars) ===== */}
            {isSearching && search.data && (
              <>
                {(['customers', 'leads', 'packages', 'blogs'] as const).map((kind) => {
                  const rows = search.data?.[kind] ?? [];
                  if (rows.length === 0) return null;
                  const Icon = KIND_ICONS[kind];
                  return (
                    <Command.Group
                      key={kind}
                      heading={
                        <GroupHeader
                          accent={KIND_ACCENT[kind]}
                          label={KIND_LABELS[kind]}
                          count={rows.length}
                        />
                      }
                      className={GROUP_CLASS}
                    >
                      {rows.map((hit) => (
                        <Command.Item
                          key={`${kind}-${hit.id}`}
                          value={`${kind}-${hit.id}-${hit.title}`}
                          onSelect={() => go(hit.to)}
                          className={ROW_CLASS}
                        >
                          <IconChip className={KIND_CHIP[kind]}>
                            <Icon className="h-[14px] w-[14px]" />
                          </IconChip>
                          <span className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate font-medium">{hit.title}</span>
                            {hit.subtitle && (
                              <span className="truncate text-[12px] text-[var(--fg-faint)]">
                                {hit.subtitle}
                              </span>
                            )}
                          </span>
                          <ChevronRight className="h-3 w-3 text-[var(--fg-faint)]" />
                        </Command.Item>
                      ))}
                    </Command.Group>
                  );
                })}

                <Command.Group
                  heading={
                    <GroupHeader
                      accent="slate"
                      label={t('shell.command.navigate')}
                      count={navItems.length}
                    />
                  }
                  className={GROUP_CLASS}
                >
                  {navItems.map((item) => (
                    <PaletteNavRow
                      key={item.id}
                      item={item}
                      label={t(`shell.nav.${item.id}`)}
                      chipClass={NAV_CHIP}
                      onSelect={() => go(item.to, item.id)}
                    />
                  ))}
                </Command.Group>
              </>
            )}
          </Command.List>
          <div className="flex gap-3.5 border-t border-[var(--ds-border)] bg-[var(--z-50)] px-3.5 py-2 text-[12px] text-[var(--fg-faint)]">
            <span>
              <span className="kbd">↑</span> <span className="kbd">↓</span>{' '}
              {t('shell.command.hintNav')}
            </span>
            <span>
              <span className="kbd">↵</span> {t('shell.command.hintSelect')}
            </span>
            <span>
              <span className="kbd">esc</span> {t('shell.command.hintClose')}
            </span>
          </div>
        </Command>
      </div>
    </div>,
    document.body,
  );
};

/**
 * Heading wrapper - removes the all-caps text styling (passed inline now)
 * so the JSX `<GroupHeader>` looks like one renders.
 */
const GROUP_CLASS = '[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:py-2 mb-1';

/** The dialog itself is on `--bg` (#fff), so we use `--z-100` (#f4f4f5)
 *  for the selected / hover row - the design system's actual "row hover"
 *  shade. Sufficient contrast against pure white. */
const ROW_CLASS =
  'flex cursor-pointer items-center gap-2.5 rounded-[var(--r-sm)] px-2.5 py-2 text-[14px] text-[var(--fg)] transition-colors data-[selected=true]:bg-[var(--z-100)] data-[selected=true]:text-[var(--fg)] hover:bg-[var(--z-50)]';

interface GroupHeaderProps {
  accent: Accent;
  label: string;
  count?: number;
}

/**
 * Coloured group header. A small accent dot + the label + an optional row-
 * count badge. Renders into Command.Group's `heading` slot so cmdk still
 * groups items semantically.
 */
const GroupHeader = ({ accent, label, count }: GroupHeaderProps): ReactElement => (
  <span className="flex items-center gap-2 text-[10.5px] font-semibold tracking-[0.07em] text-[var(--fg-muted)] uppercase">
    <span className={`h-1.5 w-1.5 rounded-full ${ACCENT_DOT[accent]}`} aria-hidden />
    <span>{label}</span>
    {typeof count === 'number' && count > 0 && (
      <span className="ml-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full border border-[var(--ds-border)] bg-[var(--z-50)] px-1.5 text-[10px] font-medium tracking-normal text-[var(--fg-muted)] normal-case tabular-nums">
        {count}
      </span>
    )}
  </span>
);

interface IconChipProps {
  className: string;
  children: React.ReactNode;
}

/**
 * Rounded coloured tile that houses each row's leading icon. The colour
 * is the only piece carrying meaning - kind, follow-up urgency, etc.
 */
const IconChip = ({ className, children }: IconChipProps): ReactElement => (
  <span
    className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md ring-1 ring-[var(--ds-border)] ${className}`}
  >
    {children}
  </span>
);

interface DueChipProps {
  label: string;
}

/**
 * Inline pill summarising follow-up urgency. The label string itself
 * indicates the urgency band ("Overdue 2d" / "Due today" / "Due in 3d");
 * we colour-code accordingly.
 */
const DueChip = ({ label }: DueChipProps): ReactElement => {
  const tone = label.startsWith('Overdue')
    ? 'bg-[var(--ds-error-bg)] text-[var(--ds-error)]'
    : label === 'Due today'
      ? 'bg-[var(--ds-warn-bg)] text-[var(--ds-warn)]'
      : 'bg-[var(--z-100)] text-[var(--fg-muted)]';
  return (
    <span
      className={`ml-1 inline-flex h-[18px] items-center rounded-full px-1.5 text-[10px] font-semibold ${tone}`}
    >
      {label}
    </span>
  );
};

interface PaletteNavRowProps {
  item: NavItemConfig;
  label: string;
  leadingIcon?: LucideIcon;
  chipClass?: string;
  onSelect: () => void;
}

const PaletteNavRow = ({
  item,
  label,
  leadingIcon,
  chipClass = NAV_CHIP,
  onSelect,
}: PaletteNavRowProps): ReactElement => {
  const Icon = leadingIcon ?? item.icon;
  return (
    <Command.Item value={`${label} ${item.to}`} onSelect={onSelect} className={ROW_CLASS}>
      <IconChip className={chipClass}>
        <Icon className="h-[14px] w-[14px]" />
      </IconChip>
      <span className="flex-1 truncate">{label}</span>
      <ChevronRight className="h-3 w-3 text-[var(--fg-faint)]" />
    </Command.Item>
  );
};

interface PaletteLeadRowProps {
  lead: LeadListItem;
  icon: LucideIcon;
  chipClass: string;
  showFollowUpDue?: boolean;
  onSelect: () => void;
}

const PaletteLeadRow = ({
  lead,
  icon: Icon,
  chipClass,
  showFollowUpDue,
  onSelect,
}: PaletteLeadRowProps): ReactElement => {
  const due = showFollowUpDue ? formatFollowUpDue(lead.nextFollowUpAt) : null;
  return (
    <Command.Item
      value={`lead-${lead.id}-${lead.reference}`}
      onSelect={onSelect}
      className={ROW_CLASS}
    >
      <IconChip className={chipClass}>
        <Icon className="h-[14px] w-[14px]" />
      </IconChip>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-center gap-1.5 truncate">
          <span className="truncate font-mono text-[13px] font-semibold">{lead.reference}</span>
          {due && <DueChip label={due} />}
        </span>
        <span className="truncate text-[12px] text-[var(--fg-faint)]">
          {[lead.title, lead.customer?.displayName].filter(Boolean).join(' · ')}
        </span>
      </span>
      <ChevronRight className="h-3 w-3 text-[var(--fg-faint)]" />
    </Command.Item>
  );
};
